import { z } from '../zod'

import type { FirestoreTimestamp } from './common'

const getDateSchema = (error: string) => {
  return z
    .union([z.date(), z.string().pipe(z.coerce.date())])
    .pipe(z.date().min(new Date(), { error: error }))
}

export const createCampaignCreationSchema = (existingCodes: string[] = []) => {
  return z
    .object({
      campaignCode: z
        .string()
        .trim()
        .min(1, 'Campaign code is required')
        .refine((code) => !existingCodes.includes(code), {
          message: 'Campaign with this code already exists.',
        }),
      emailTemplate: z.string().min(1, 'Email template is required'),
      dueDate: getDateSchema('Due date must be in the future'),
      evaluationEndDate: getDateSchema(
        'Evaluation end date must be in the future'
      ),
      countdownExpirationDate: getDateSchema(
        'Countdown expiration date must be in the future'
      ).nullishCoalescingUndefined(),
      from: z.string().nullishCoalescingUndefined(),
      freeOnly: z.boolean(),
    })
    .refine(
      (data) =>
        !data.countdownExpirationDate ||
        data.countdownExpirationDate > data.dueDate,
      {
        error: 'Countdown expiration date must be later than the due date.',
        path: ['countdownExpirationDate'],
      }
    )
    .refine((data) => data.evaluationEndDate > data.dueDate, {
      error: 'Evaluation end date must be later than the due date.',
      path: ['evaluationEndDate'],
    })
}

export type CampaignCreationPayload = z.infer<
  ReturnType<typeof createCampaignCreationSchema>
>

export enum EmailCampaignStatus {
  CREATED = 'CREATED',
  AUDIENCE_CREATED = 'AUDIENCE_CREATED',
  SCHEDULED = 'SCHEDULED',
  EXECUTED = 'EXECUTED',
  ERROR = 'ERROR',
}

export type EmailCampaign = {
  campaign_code: string
  email_template_id: string
  from?: string
  free_only?: boolean
  audience_size?: number
  audience_table: string
  due_date: FirestoreTimestamp
  evaluation_end_date: FirestoreTimestamp
  countdown_expiration_date?: FirestoreTimestamp
  status: EmailCampaignStatus
  status_message?: string
}

type EmailCampaignStatistics = {
  subscribedUserCount?: number
  revenue?: number
  predictedRevenue?: number
}

export type EmailCampaignResult = {
  sentEmailCount?: number
  bouncedEmailCount?: number
  openedEmailCount?: number
  clickedEmailCount?: number
  unsubscribeClickedEmailCount?: number
  ctaClickedEmailCount?: number
  audience: EmailCampaignStatistics
  controlGroup: EmailCampaignStatistics
  netPredictedRevenue: number
  dueDate: FirestoreTimestamp
  evaluationEndDate: FirestoreTimestamp
}

export type AudienceUserData = {
  user_id: string
  email?: string
  name: string
  language: string
  variant: number
  domain_variant?: string
}

export type EmailAudienceBatchPayload = {
  userData: AudienceUserData[]
  countdownExpirationDateString?: string
  dueDateString: string
  campaignId: string | null
  batchNumber: number
  totalBatches: number
} & Pick<
  CampaignCreationPayload,
  'campaignCode' | 'emailTemplate' | 'from' | 'freeOnly'
>

export enum EmailCampaignGroupVariant {
  Control = 0,
  Audience = 1,
}

export enum CtaTarget {
  DynamicLink = 'dynamic_link_promo',
  WebGift = 'web_gift',
  WebOnboarding = 'web_onboarding',
}
